import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonSizes, ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import {
  MicrosoftDialPlanFieldConfig,
  PrivilegedTenant,
  Type,
  UserGroupPermissions,
} from '../../../shared/models/generated/ziroModels';
import { UiUserPermissionTypes, UserPermissionsContext } from '../../contexts/user-permissions.context';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { UserPermissionsModalComponent } from './user-permissions-modal.component';
import { UserPermissionsRolePipe } from './user-permissions-role.pipe';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { MicrosoftLegacyGlobalDialPlansFieldConfigContext } from '../../../shared/contexts/microsoft-legacy-global-dial-plans-field-config.context';
import { MicrosoftTenantsStateContext } from '../../../shared/contexts/microsoft-tenants-state.context';

export interface UserPermissionsRow extends DatatableRow {
  type: string;
  userPrincipalName: string;
  role: string;
  permittedDialPlanGroups: string[];
}

@Component({
  selector: 'ziro-user-permissions',
  templateUrl: './user-permissions.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [UserPermissionsRolePipe],
  standalone: false,
})
export class UserPermissionsComponent implements OnInit, OnDestroy {
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _microsoftUserPermissionsContext = inject(UserPermissionsContext);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _userPermissionsRolePipe = inject(UserPermissionsRolePipe);
  private _microsoftLegacyGlobalDialPlansFieldConfigContext = inject(MicrosoftLegacyGlobalDialPlansFieldConfigContext);
  private readonly _microsoftTenantsStateContext = inject(MicrosoftTenantsStateContext);

  tableAutomationId = 'user-permissions-table';
  columns: DatatableColumn<UserPermissionsRow>[] = [
    {
      name: 'type',
      label: 'tkey;admin.system.user_permissions.type.label',
      columnSize: 'col-sm-2',
    },
    {
      name: 'userPrincipalName',
      label: 'tkey;admin.system.user_permissions.user_principal_name.label',
      columnSize: 'col-sm-3',
    },
    {
      name: 'role',
      label: 'tkey;admin.system.user_permissions.roles.label',
      columnSize: 'col-sm-2',
    },
    {
      name: 'permittedDialPlanGroups',
      label: 'tkey;admin.system.user_permissions.permitted_dialplan_groups.label',
      sortFn: (a: string[], b: string[]) => (a[0] ?? '').localeCompare(b[0] ?? ''),
      columnSize: 'col-sm-4',
    },
  ];
  rows: UserPermissionsRow[] = [];
  buttonStyles = ButtonStyles;
  buttonSizes = ButtonSizes;
  ziroIcons = ZiroIcons;
  isLoading = true;
  roles = [UiUserPermissionTypes.ADMINISTRATOR, UiUserPermissionTypes.HELPDESK] as string[];
  dialPlanGroups: string[];
  userPermissions: UserGroupPermissions[] = [];

  private _dialPlanGroups: MicrosoftDialPlanFieldConfig[] = [];
  private _microsoftTenants: PrivilegedTenant[];
  private _subscriptions = new Subscription();
  private _isTenantConfigured: boolean;

  ngOnInit(): void {
    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.system.user_permissions.title' }]);

    const subscription = combineLatest([
      this._microsoftUserPermissionsContext.state$,
      this._microsoftLegacyGlobalDialPlansFieldConfigContext.state$,
      this._microsoftTenantsStateContext.fieldConfigState$,
    ]).subscribe(([userGroupPermissions, microsoftDialPlanGroup, microsoftTenants]) => {
      this.userPermissions = userGroupPermissions;
      this._dialPlanGroups = microsoftDialPlanGroup;
      this._microsoftTenants = microsoftTenants.privilegedTenants;
      this.dialPlanGroups = this._dialPlanGroups.map((group: MicrosoftDialPlanFieldConfig) => {
        return group.name;
      });

      this._isTenantConfigured = this._microsoftTenants.length > 0;

      this._generateTableRows();
      this._initBottomNav();
      this.isLoading = false;
    });
    this._subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._subscriptions.unsubscribe();
  }

  onDeleteClicked(userPermission: UserGroupPermissions): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        promptBody: this._translateService.instant('tkey;admin.system.user_permissions.delete.modal.message', {
          upn: userPermission.name,
        }),
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteGroup(userPermission);
            },
          },
        ],
      },
    };
    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  isTypeMatch(filters: string[], row: UserPermissionsRow): boolean {
    return !filters.length || filters.map((filter) => filter.toLowerCase()).includes(row.type.toLowerCase());
  }

  isRoleMatch(filters: string[], row: UserPermissionsRow): boolean {
    return !filters.length || filters.includes(row.role);
  }

  isPermittedDpgMatch(filters: string[], row: UserPermissionsRow): boolean {
    return !filters.length || !!filters.find((value) => row.permittedDialPlanGroups.includes(value));
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'user-permissions-add-group',
          label: 'tkey;admin.system.user_permissions.form.add_group.label',
          icon: ZiroIcons.ADD_USER_GROUP_TO_LIST,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'user-permissions-add-group-button',
          state: {
            buttonDisableState: {
              disabled: !this._isTenantConfigured,
              tooltipKey: 'tkey;admin.system.user_permissions.table.no_tenant_configured.tooltip',
            },
            tooltipVisible: !this._isTenantConfigured,
          },
          cb: () => this._onAddClicked(Type.GROUP),
        },
        {
          id: 'user-permissions-add-user',
          label: 'tkey;admin.system.user_permissions.form.add_user.label',
          icon: ZiroIcons.ADD_USER_TO_LIST,
          buttonClass: ButtonStyles.PRIMARY,
          dataAutomation: 'user-permissions-add-user-button',
          cb: () => this._onAddClicked(Type.USER),
        },
      ]),
    );
  }

  private _deleteGroup(userPermission: UserGroupPermissions): Observable<void> {
    return this._microsoftUserPermissionsContext.delete(userPermission);
  }

  private _generateTableRows(): void {
    this.rows = this.userPermissions.map((permission: UserGroupPermissions) => {
      return {
        type: permission.type,
        userPrincipalName: permission.name,
        role: this._userPermissionsRolePipe.transform(permission.role),
        permittedDialPlanGroups: permission.permittedDialPlanGroups,
        html: {
          type: `<span class="text-capitalize">${permission.type.toLowerCase()}</span>`,
        },
        badges: {
          permittedDialPlanGroups: {
            values: permission.permittedDialPlanGroups.map((dialPlanGroup) => {
              return { label: dialPlanGroup };
            }),
          },
        },
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_PRIMARY,
            dataAutomation: 'user-permissions-row-edit',
            icon: ZiroIcons.EDIT,
            disabled: !this._isTenantConfigured && permission.type === Type.GROUP,
            disabledTooltip: this._isTenantConfigured
              ? ''
              : 'tkey;admin.system.user_permissions.table.no_tenant_configured.tooltip',
            isTooltipDisabled: this._isTenantConfigured || permission.type === Type.USER,
            onClick: () => this._onEditClicked(permission),
          },
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'user-permissions-row-delete',
            icon: ZiroIcons.DELETE_SMALL,
            onClick: () => this.onDeleteClicked(permission),
          },
        ],
      };
    });
  }

  private _onAddClicked(type: Type): void {
    this._openModal({ name: '', type: type, role: null, permittedDialPlanGroups: [], tenantId: null });
  }

  private _onEditClicked(permission: UserGroupPermissions): void {
    this._openModal(permission);
  }

  private _openModal(permission: UserGroupPermissions): void {
    const options = {
      size: 'lg',
      bodyClass: UserPermissionsModalComponent,
      modalViewProperties: {
        title: this._determineTitleForModal(permission),
        userPermission: permission,
        allUserPermissions: this.userPermissions,
        dialPlanGroups: this._dialPlanGroups,
        isTenantConfigured: this._isTenantConfigured,
        modalBodyClass: 'overflow-visible',
        microsoftTenants: this._microsoftTenants,
        submitFn: (requestBody: UserGroupPermissions): Observable<void> => {
          return permission.name
            ? this._microsoftUserPermissionsContext.put(requestBody)
            : this._microsoftUserPermissionsContext.post(requestBody);
        },
      },
    };
    this._ziroModalService.openDetailedModal(() => options.modalViewProperties, options);
  }

  private _determineTitleForModal(permission: UserGroupPermissions): string {
    const isExisting = !!permission.name;
    const type = permission.type;

    if (type === Type.USER) {
      return isExisting
        ? 'tkey;admin.system.user_permissions.modify_user_permissions.label'
        : 'tkey;admin.system.user_permissions.add_user_permissions.label';
    } else {
      return isExisting
        ? 'tkey;admin.system.user_permissions.modify_group_permissions.label'
        : 'tkey;admin.system.user_permissions.add_group_permissions.label';
    }
  }
}
